import { type ReactNode, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { copiarAlPortapapeles } from '../lib/portapapeles';

/** Ojo minimalista (solo líneas) para mostrar/ocultar contraseña. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.8 12S5.3 5.2 12 5.2 22.2 12 22.2 12 18.7 18.8 12 18.8 1.8 12 1.8 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="4" y1="4" x2="20" y2="20" />}
    </svg>
  );
}

/**
 * Input de contraseña con el ojito para verla (pedido 2026-08-10: "en todos lados donde van
 * los passwords"). Mismo ojo que Field; acepta las props estándar de un <input> así que
 * reemplaza un `type="password"` crudo sin tocar nada más. El wrapper toma el `style` del
 * llamador (ancho/maxWidth) y el input llena el wrapper.
 */
export function PasswordInput({ className = 'input', style, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { className?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', ...style }}>
      <input {...props} className={className} type={show ? 'text' : 'password'} style={{ width: '100%', paddingRight: 38 }} />
      <button
        type="button"
        aria-label={show ? 'Hide' : 'Show'}
        tabIndex={-1}
        onClick={() => setShow((s) => !s)}
        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #9aa)', padding: 4, display: 'inline-flex' }}
      >
        <EyeIcon off={!show} />
      </button>
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="field">
      {label}
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          type={isPassword && show ? 'text' : type}
          value={value}
          placeholder={placeholder}
          required={required}
          style={isPassword ? { paddingRight: 38 } : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        {isPassword && (
          <button
            type="button"
            aria-label={show ? 'Hide' : 'Show'}
            onClick={() => setShow((s) => !s)}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #9aa)', padding: 4, display: 'inline-flex' }}
          >
            <EyeIcon off={!show} />
          </button>
        )}
      </div>
    </label>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}

export function useAsync() {
  /* placeholder for symmetry — pages use local state */
}

/**
 * Botón de COPIAR al portapapeles. Vivía privado dentro del ClaimDrawer; se subió acá cuando hizo
 * falta el mismo botón en Claim Review, porque duplicarlo era garantizar que uno de los dos se
 * quedara viejo. Confirma con un ✓ por un segundo: sin acuse, la gente copia dos y tres veces
 * porque no sabe si funcionó.
 */
export function CopyBtn({ value, title }: { value: string | null | undefined; title?: string }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const texto = value;
  // Por el helper, y el ✓ SOLO si de verdad copió: el `.then()` de antes prendía el acuse y dejaba
  // el rechazo sin atajar — de este botón salía el `NotAllowedError: Document is not focused` que
  // Sentry reportó desde /dept/supplements/queue/First Estimate.
  const copiar = async () => {
    if (await copiarAlPortapapeles(texto)) { setDone(true); setTimeout(() => setDone(false), 1200); }
  };
  return (
    <button
      className="btn ghost sm"
      title={title || 'Copy'}
      style={{ padding: '1px 6px', fontSize: 'var(--fs-2xs)', lineHeight: 1.2 }}
      onClick={(e) => { e.stopPropagation(); void copiar(); }}
    >
      {done ? <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : <Copy size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}
    </button>
  );
}
